import { calculateDateToCourse, isVacancyBelowThreshold } from './courseHelper';

export const getDateRange = (
  localStartDate,
  localEndDate,
  withYear = false
) => {
  const allMonths = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const startDate = new Date(localStartDate);
  const endDate = new Date(localEndDate);

  const startDayOfMonth = startDate.getDate();
  const startMonth = allMonths[startDate.getMonth()];

  const endDayOfMonth = endDate.getDate();
  const endMonth = allMonths[endDate.getMonth()];

  const sameStartAndEndDay =
    startDayOfMonth === endDayOfMonth && startMonth === endMonth;

  let date;

  if (sameStartAndEndDay) {
    date = `${startDayOfMonth} ${startMonth}`;
  } else if (startMonth === endMonth) {
    date = `${startDayOfMonth} - ${endDayOfMonth} ${endMonth}`;
  } else {
    date = `${startDayOfMonth} ${startMonth} - ${endDayOfMonth} ${endMonth}`;
  }

  if (withYear) {
    return `${date} ${endDate.getFullYear()}`;
  }

  return date;
};

export const getAlertMessage = (course) => {
  if (course?.alertMessage) return course.alertMessage;

  const { CourseOfferStatus, MaxRegistrations, VacancyCount } = course;
  let msg = '';

  if (CourseOfferStatus !== 'Active') {
    msg = 'No more seats left';
    return msg;
  }

  const lessThan20percentSeatsLeft = isVacancyBelowThreshold(
    MaxRegistrations,
    VacancyCount,
    20
  );

  if (lessThan20percentSeatsLeft) {
    msg =
      VacancyCount > 1
        ? `${VacancyCount} spots left!`
        : `${VacancyCount} spot left!`;
    return msg;
  }

  const daysLeftToCourse = calculateDateToCourse(course).days;

  if (daysLeftToCourse < 10) {
    msg =
      daysLeftToCourse > 1
        ? `${daysLeftToCourse} days left!`
        : `${daysLeftToCourse} day left!`;
    return msg;
  }

  return msg;
};

export const getTime = (time) => {
  let convertedTime = time
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h12',
    })
    .toUpperCase();

  // Edge case in chrome
  if (convertedTime.includes('00:') && convertedTime.includes('PM')) {
    convertedTime = convertedTime.replace('00:', '12:');
  }
  return convertedTime;
};

const getDayAndTime = (course) => {
  const { localStartDate, localEndDate, frequency } = course;
  const dayRange = frequency?.localFrequency ?? '';

  const startDate = new Date(localStartDate);
  const endDate = new Date(localEndDate);

  const startTime = getTime(startDate);

  const endTime = getTime(endDate);

  const timeRange = `${startTime} - ${endTime}`;

  const timezoneOffset = new Date().getTimezoneOffset() / 60;
  const gmtSign = timezoneOffset < 0 ? '+' : '-';
  const gmtOffset = `${gmtSign}${Math.abs(timezoneOffset)}`;

  return {
    dayRange,
    timeRange,
    gmtOffset,
  };
};

export const getLocallyFormattedOfferingData = (courseOffering) => {
  const { localStartDate, localEndDate } = courseOffering;
  const dateToRender = getDateRange(localStartDate, localEndDate);
  const { dayRange, timeRange, gmtOffset } = getDayAndTime(courseOffering);

  return {
    dateToRender,
    dayRange,
    timeRange,
    gmtOffset,
    ...courseOffering,
  };
};

export const countdownExpires = (countdown) => {
  const { days, hrs, mins, secs } = countdown || {};
  return days === 0 && hrs === 0 && mins === 0 && secs === 0;
};

export const groupCourseOffersByDate = (courseOfferings) => {
  let formattedCalData = {};

  courseOfferings.forEach((course) => {
    const { localStartDate } = course;
    const startDate = new Date(localStartDate);
    const year = startDate.getFullYear();
    const month = startDate.getMonth();
    const day = startDate.getDate();

    if (!formattedCalData[year]) {
      formattedCalData[year] = {};
    }

    if (!formattedCalData[year][month]) {
      formattedCalData[year][month] = {};
    }

    if (!formattedCalData[year][month][day]) {
      formattedCalData[year][month][day] = [];
    }

    formattedCalData[year][month][day].push(course);
  });

  return formattedCalData;
};

// Filter out only course that has vacancy
export const isCourseOfferAvailable = (courseOffering) =>
  courseOffering.vacancyCount > 0;

export const discountTypeMap = {
  percent_off: '%',
  value_off: 'USD',
};

export const formatCourseTime = (courseTime) => {
  const courseTimeArr = courseTime.split('-');
  const courseStart = courseTimeArr[0].trim();
  const courseEnd = courseTimeArr[1].trim();
  return formatCourseTimeStr(courseStart, courseEnd);
};

export const formatCourseTimeStr = (courseStart, courseEnd) => {
  if (courseStart === courseEnd) {
    return courseStart;
  } else {
    const courseStartPeriod = courseStart.split(' ');
    const courseEndPeriod = courseEnd.split(' ');
    if (courseStartPeriod[1] === courseEndPeriod[1]) {
      return `${courseStartPeriod[0]}-${courseEndPeriod[0]} ${courseStartPeriod[1]}`;
    } else {
      return `${courseStart}-${courseEnd}`;
    }
  }
};

export const getTimezoneId = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};
